import { useState, useEffect } from 'react';
import { getPublicBaseUrl } from '@/lib/api';
import { Copy, Code, ExternalLink, Check, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { useToast } from '@/hooks/use-toast';

interface ChatbotEmbedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatbotId: string;
  chatbotName: string;
  widgetConfig?: {
    primary_color?: string;
    position?: string;
    header_text?: string;
  };
}

export default function ChatbotEmbedModal({
  open,
  onOpenChange,
  chatbotId,
  chatbotName,
  widgetConfig
}: ChatbotEmbedModalProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const [customDomain, setCustomDomain] = useState('');
  
  // Task #3257: unica fonte per l'origin pubblico (product-aware via origin live).
  const baseUrl = getPublicBaseUrl();
  
  const widgetUrl = `${baseUrl}/chatbot/widget/${chatbotId}`;
  
  const iframeCode = `<!-- EDG VoIP Chatbot Widget -->
<iframe
  src="${widgetUrl}"
  style="position: fixed; bottom: 20px; right: 20px; width: 400px; height: 600px; border: none; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); z-index: 9999;"
  allow="microphone"
></iframe>`;

  const scriptCode = `<!-- EDG VoIP Chatbot Widget -->
<script>
(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${widgetUrl}';
  iframe.style.cssText = 'position:fixed;bottom:20px;${widgetConfig?.position === 'bottom-left' ? 'left' : 'right'}:20px;width:400px;height:600px;border:none;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,0.15);z-index:9999;display:none;';
  iframe.id = 'edg-chatbot-widget';
  iframe.allow = 'microphone';
  document.body.appendChild(iframe);
  
  var bubble = document.createElement('div');
  bubble.innerHTML = '<svg width="24" height="24" fill="white" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';
  bubble.style.cssText = 'position:fixed;bottom:20px;${widgetConfig?.position === 'bottom-left' ? 'left' : 'right'}:20px;width:60px;height:60px;background:${widgetConfig?.primary_color || '#3b82f6'};border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(0,0,0,0.3);z-index:10000;';
  bubble.onclick = function() {
    var w = document.getElementById('edg-chatbot-widget');
    if(w.style.display === 'none') {
      w.style.display = 'block';
      bubble.style.display = 'none';
    }
  };
  document.body.appendChild(bubble);
  
  window.addEventListener('message', function(e) {
    if(e.data === 'edg-chatbot-close') {
      document.getElementById('edg-chatbot-widget').style.display = 'none';
      bubble.style.display = 'flex';
    }
  });
})();
</script>`;

  const bubbleOnlyCode = `<!-- EDG VoIP Chatbot Bubble -->
<div id="edg-chatbot-container"></div>
<script src="${baseUrl}/chatbot/embed.js" data-chatbot-id="${chatbotId}"></script>`;

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast({ title: 'Copiato!', description: 'Codice copiato negli appunti' });
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast({ title: 'Errore', description: 'Impossibile copiare', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Codice Embed - {chatbotName}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="script" className="flex-1 overflow-hidden">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="script">Script (Consigliato)</TabsTrigger>
            <TabsTrigger value="iframe">Iframe</TabsTrigger>
            <TabsTrigger value="bubble">Bubble Only</TabsTrigger>
          </TabsList>

          <div className="overflow-y-auto flex-1 py-4" style={{ maxHeight: 'calc(85vh - 180px)' }}>
            <TabsContent value="script" className="mt-0 space-y-4">
              <Card className="border-border">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    Codice JavaScript
                    <Badge variant="secondary" className="text-xs bg-green-500/20 text-green-400">Consigliato</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Incolla questo codice prima della chiusura del tag <code className="bg-gray-800 px-1 rounded">&lt;/body&gt;</code> del tuo sito.
                    Include bubble animata e gestione apertura/chiusura.
                  </p>
                  <div className="relative">
                    <pre className="bg-gray-900 p-4 rounded-lg text-xs overflow-x-auto max-h-64">
                      <code className="text-green-400">{scriptCode}</code>
                    </pre>
                    <Button
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(scriptCode, 'script')}
                    >
                      {copied === 'script' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="iframe" className="mt-0 space-y-4">
              <Card className="border-border">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Codice Iframe</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Soluzione semplice: incolla questo codice nel tuo HTML. Il widget sarà sempre visibile.
                  </p>
                  <div className="relative">
                    <pre className="bg-gray-900 p-4 rounded-lg text-xs overflow-x-auto">
                      <code className="text-blue-400">{iframeCode}</code>
                    </pre>
                    <Button
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(iframeCode, 'iframe')}
                    >
                      {copied === 'iframe' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="bubble" className="mt-0 space-y-4">
              <Card className="border-border">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Bubble Only (Caricamento Lazy)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Soluzione avanzata con caricamento lazy. Il widget viene caricato solo quando l'utente clicca la bubble.
                  </p>
                  <div className="relative">
                    <pre className="bg-gray-900 p-4 rounded-lg text-xs overflow-x-auto">
                      <code className="text-purple-400">{bubbleOnlyCode}</code>
                    </pre>
                    <Button
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(bubbleOnlyCode, 'bubble')}
                    >
                      {copied === 'bubble' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>

        <div className="border-t border-border pt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MessageSquare className="h-4 w-4" />
            ID Chatbot: <code className="bg-gray-800 px-2 py-1 rounded text-xs">{chatbotId}</code>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open(widgetUrl, '_blank')}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Anteprima Widget
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
